// src/components/products/ProductForm/helpers/payloadBuilder.js

import { formatTime, formatDate } from "../../../../utils/forms/dateTimeHelpers";
import { getProductTypeInfo } from "../../../../utils/forms/productFormHelpers";
import { TOUR_TYPES } from "./formConstants";

/**
 * ✅ Fill empty language fields from the other language
 * إذا كانت لغة فارغة، نملأها من اللغة الأخرى
 */
const fillEmptyLanguageFields = (field) => {
    if (!field || typeof field !== 'object') return field;

    const ar = (field.ar || '').trim();
    const en = (field.en || '').trim();

    // إذا كانت العربية فارغة والإنجليزية موجودة، ملأ العربية
    if (!ar && en) {
        return { ar: en, en };
    }

    // إذا كانت الإنجليزية فارغة والعربية موجودة، ملأ الإنجليزية
    if (ar && !en) {
        return { ar, en: ar };
    }

    // إذا كانت كلاهما موجودة أو كلاهما فارغة، أرجع كما هي
    return { ar: ar || '', en: en || '' };
};

/**
 * ✅ Build base payload (common for all product types)
 */
const buildBasePayload = (formData) => {
    return {
        product_type_id: formData.product_type_id,
        category_id: formData.category_id,
        transportation_id: formData.transportation_id,
        city_id: formData.city_id || null,
        service_id: formData.service_id,
        supplier_ids: formData.supplier_ids || [],
        title: fillEmptyLanguageFields(formData.title),
        pricing_unit_id: formData.pricing_unit_id,
        price_type: fillEmptyLanguageFields(formData.price_type),
        operating_days: fillEmptyLanguageFields(formData.operating_days),
        operating_type: fillEmptyLanguageFields(formData.operating_type),
        short_description: fillEmptyLanguageFields(formData.short_description),
        summary: fillEmptyLanguageFields(formData.summary),
        inclusions: fillEmptyLanguageFields(formData.inclusions),
        exclusions: fillEmptyLanguageFields(formData.exclusions),
        cancellation_and_change_policy: fillEmptyLanguageFields(formData.cancellation_and_change_policy),
        locations: fillEmptyLanguageFields(formData.locations),
        cover_image: formData.cover_image,
        images: formData.images,
        currency_id: formData.currency_id,
        rate_availability: formData.rate_availability,
        rate_availability_start_date: formatDate(formData.rate_availability_start_date),
        rate_availability_end_date: formatDate(formData.rate_availability_end_date),
    };
};

/**
 * ✅ Build Transfer-specific payload
 */
const buildTransferPayload = (basePayload, formData) => {
    return {
        ...basePayload,
        operating_hours_start_time: formatTime(formData.operating_hours_start_time),
        operating_hours_end_time: formatTime(formData.operating_hours_end_time),
        itinerary: fillEmptyLanguageFields(formData.itinerary),
        notes: fillEmptyLanguageFields(formData.notes),
    };
};

/**
 * ✅ Build Tour Default type payload
 */
const buildTourDefaultPayload = (basePayload, formData) => {
    return {
        ...basePayload,
        package_id: formData.package_id,
        type: formData.type,
        highlights: fillEmptyLanguageFields(formData.highlights),
        long_description: fillEmptyLanguageFields(formData.long_description),
        what_to_bring: fillEmptyLanguageFields(formData.what_to_bring),
        voucher_notes: fillEmptyLanguageFields(formData.voucher_notes),
        free_transfer_areas: fillEmptyLanguageFields(formData.free_transfer_areas),
        extra_charge_transfer_areas: fillEmptyLanguageFields(formData.extra_charge_transfer_areas),

        // Default fields
        default_itinerary: fillEmptyLanguageFields(formData.default_itinerary),
        operating_hours_start_time: formatTime(formData.operating_hours_start_time),
        operating_hours_end_time: formatTime(formData.operating_hours_end_time),

        // Clear Morning & Evening fields
        morning_operating_hours_start_time: null,
        morning_operating_hours_end_time: null,
        evening_operating_hours_start_time: null,
        evening_operating_hours_end_time: null,
        morning_itinerary: null,
        evening_itinerary: null,
    };
};

/**
 * ✅ Build Tour Morning & Evening type payload
 */
const buildTourMorningEveningPayload = (basePayload, formData) => {
    return {
        ...basePayload,
        package_id: formData.package_id,
        type: formData.type,
        highlights: fillEmptyLanguageFields(formData.highlights),
        long_description: fillEmptyLanguageFields(formData.long_description),
        what_to_bring: fillEmptyLanguageFields(formData.what_to_bring),
        voucher_notes: fillEmptyLanguageFields(formData.voucher_notes),
        free_transfer_areas: fillEmptyLanguageFields(formData.free_transfer_areas),
        extra_charge_transfer_areas: fillEmptyLanguageFields(formData.extra_charge_transfer_areas),

        // Morning & Evening fields
        morning_operating_hours_start_time: formatTime(formData.morning_operating_hours_start_time),
        morning_operating_hours_end_time: formatTime(formData.morning_operating_hours_end_time),
        evening_operating_hours_start_time: formatTime(formData.evening_operating_hours_start_time),
        evening_operating_hours_end_time: formatTime(formData.evening_operating_hours_end_time),
        morning_itinerary: fillEmptyLanguageFields(formData.morning_itinerary),
        evening_itinerary: fillEmptyLanguageFields(formData.evening_itinerary),

        // Clear Default fields
        default_itinerary: null,
        operating_hours_start_time: null,
        operating_hours_end_time: null,
    };
};

/**
 * ✅ Main payload builder function
 */
export const buildPayload = (formData) => {
    const { isTransfer, isTour } = getProductTypeInfo(formData.selectedProductType);
    const basePayload = buildBasePayload(formData);

    // Transfer payload
    if (isTransfer) {
        return buildTransferPayload(basePayload, formData);
    }

    // Tour payload
    if (isTour) {
        if (formData.type === TOUR_TYPES.DEFAULT) {
            return buildTourDefaultPayload(basePayload, formData);
        }

        if (formData.type === TOUR_TYPES.MORNING_EVENING) {
            return buildTourMorningEveningPayload(basePayload, formData);
        }
    }

    return basePayload;
};
